import React, { useState, useEffect } from "react";
import Footer from "./Footer";
import {
  CircleCheck,
  RefreshCcw,
  Camera,
  CheckCircle,
  ArrowLeftRight,
} from "lucide-react";
import { Hourglass } from "react-loader-spinner";
import useStickyButton from "../hooks/useStickyButton";
import isMobileDevice from "../../../utils/isMobileDevice";
import DesktopQrModal from "../../../components/DesktopQrModal";

const CheckQualitySelfieWithMyKad = (props) => {
  const { isSubmitting, imageBuffer, onRetake, onConfirm } = props;

  // Safe access
  const global = {};
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const highlightTextColor = global?.highlightText?.fontColor || "#2563eb"; // blue-600
  const buttonBgColor = global?.button?.backgroundColor || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const retakeButtonTextColor = global?.button?.fontColor || "#3b82f6"; // blue-500
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white
  const cardBgColor = global?.card?.backgroundColor || "#f3f4f6"; // gray-50
  const cardOpacity = global?.card?.opacity || "1"; // default opacity
  const flipButtonBgColor = global?.button?.backgroundColor || "#ffffff"; // white
  const flipButtonTextColor = global?.button?.fontColor || "#6b7280"; // gray-500

  const inputRef = React.useRef(null);
  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();

  const [imageBlob, setImageBlob] = useState();
  const [showDesktopNotice, setShowDesktopNotice] = useState(false);

  const handleRetake = () => {
    if (!isMobileDevice()) {
      setShowDesktopNotice(true);
      return;
    }

    setShowDesktopNotice(false);
    inputRef.current?.click();
  };

  const onChange = async (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);

    reader.onload = async () => {
      onRetake(reader.result);
    };
  };

  useEffect(() => {
    if (imageBuffer) {
      setImageBlob(new Blob([imageBuffer], { type: "image/jpeg" }));
    }
  }, [imageBuffer]);

  const onMirror = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const img = new Image();
    img.src = URL.createObjectURL(imageBlob);
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      ctx.translate(img.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        setImageBlob(blob);
      }, "image/jpeg");
    };
  };

  return (
    <div
      style={{
        background: pageBgColor,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "1rem",
        height: "100%",
      }}
    >
      {/* Header section */}
      <h2
        style={{
          fontSize: "1.25rem",
          fontWeight: "bold",
          marginBottom: "1rem",
          textAlign: "center",
          color: textColor,
        }}
      >
        {" "}
        Review your
        <span style={{ color: highlightTextColor }}> Selfie with MyKad</span>
      </h2>

      {/* Content area with Apple-style spacing */}
      <div>
        {/* Image container with soft shadow and rounded corners */}
        <div className="flex flex-col items-center mt-4 mb-4">
          {imageBlob ? (
            <img
              src={URL.createObjectURL(imageBlob)}
              className="h-60 md:h-64 lg:h-80 w-auto rounded-lg shadow-md shadow-black"
            />
          ) : null}
          <p
            style={{
              fontWeight: "bold",
              textAlign: "center",
              marginTop: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: textColor,
            }}
          >
            Your Selfie with MyKad
          </p>

          {!isSubmitting ? (
            <div className="flex items-center mt-4">
              <button
                style={{
                  border: "1px solid #D1D5DB", // gray-300
                  padding: "0.5rem 1rem",
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: flipButtonBgColor,
                  color: flipButtonTextColor,
                }}
                onClick={onMirror}
              >
                Flip
                <ArrowLeftRight className="ml-2" />
              </button>
            </div>
          ) : null}
        </div>

        {/* Requirements section with iOS-style icons */}
        <div
          style={{
            background: cardBgColor,
            opacity: cardOpacity,
            borderRadius: "0.75rem",
            padding: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          <p
            style={{
              fontSize: "0.875rem",
              marginBottom: "0.75rem",
              fontWeight: "500", // font-medium
              color: textColor, // text-gray-500
            }}
          >
            PLEASE VERIFY THESE IMPORTANT DETAILS:
          </p>
          <div className="space-y-4 mt-4">
            <ul className="space-y-2">
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  Your MyKad is clearly visible and readable
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  Your face is clearly visible and not covered
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  No one else appears in the photo
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  The image is correctly oriented <br />
                  (not mirrored)
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      {/* Action buttons area */}
      {isSubmitting ? (
        <div className="flex flex-col justify-center items-center mb-6">
          <Hourglass height={"32"} width={"32"} color="#1E40AF" />
          <p style={{ color: textColor, marginTop: "0.5rem" }}>
            Please wait while we upload your photo...
          </p>
        </div>
      ) : (
        <div 
          ref={buttonContainerRef}
          className="px-6 mb-6"
          style={{
            position: "relative",
            bottom: "auto",
            left: "auto",
            right: "auto",
            padding: "0 1.5rem",
            background: "transparent",
            zIndex: "auto",
            transition: "all 0.3s ease-in-out",
            pointerEvents: "auto",
          }}
        >
          <button
            style={{
              background: buttonBgColor,
              color: buttonTextColor,
              width: "66.666667%",
              padding: "0.75rem 1rem",
              borderRadius: "9999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              margin: "0 auto",
              transition: "transform 0.15s ease-in-out",
              pointerEvents: "auto",
            }}
            onClick={() => onConfirm(imageBlob)}
            disabled={isSubmitting}
            onMouseDown={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(0.95)')}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onTouchStart={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(0.95)')}
            onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Confirm
            <CircleCheck className="ml-2" />
          </button>
          <button
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              paddingTop: "0.75rem", // py-3
              paddingBottom: "0.75rem",
              fontWeight: 500, // font-medium
              fontSize: "1rem", // text-base
              marginTop: "0.5rem", // mt-2
              transition: "color 0.15s ease-in-out",
              color: retakeButtonTextColor, // text-blue-500
              pointerEvents: "auto",
            }}
            onClick={handleRetake}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Retake
            <Camera className="ml-2" />
          </button>
        </div>
      )}

      <DesktopQrModal
        open={showDesktopNotice}
        onClose={() => setShowDesktopNotice(false)}
      />

      <input
        ref={inputRef}
        accept="image/*"
        type="file"
        capture="environment"
        hidden
        onChange={onChange}
      />

      <Footer
        style={{
          fontSize: "0.75rem",
          color: textColor,
          marginTop: "1rem",
        }}
      />
      {/* </div> */}
      {/* // </div> */}
      {/* <h2 className="text-xl font-bold mb-2">
        Review your
        <span className="text-blue-700"> selfie with MyKad</span>
      </h2>
      <p className="text-gray-500 my-4 text-left max-w-prose">Please verify these important details</p>

      <ul className="space-y-2">
        <li className="flex items-center space-x-2">
          <CircleCheck className="text-green-500" />
          <span className="text-gray-500">Your MyKad is clearly visible and readable</span>
        </li>
        <li className="flex items-center space-x-2">
          <CircleCheck className="text-green-500" />
          <span className="text-gray-500">Your face is clearly visible and not covered</span>
        </li>
        <li className="flex items-center space-x-2">
          <CircleCheck className="text-green-500" />
          <span className="text-gray-500">No one else appears in the photo</span>
        </li>
        <li className="flex items-center space-x-2">
          <CircleCheck className="text-green-500" />
          <span className="text-gray-500">The image is correctly oriented (not mirrored)</span>
        </li>
      </ul>

      <div className="flex flex-col items-center mt-8 mb-8">
        {imageBlob ? (
          <img
            src={URL.createObjectURL(imageBlob)}
            className="h-60 md:h-64 lg:h-80 w-auto rounded-lg shadow-md shadow-black"
          />
        ) : null}
        <p className="text-gray-500 font-bold text-center mt-2 flex items-center justify-center">
          Your Selfie with MyKad
        </p>

        {!isSubmitting ? (
          <div className="flex items-center mt-4">
            <button
              className="bg-white border border-gray-300 text-gray-500 py-2 px-4 rounded-full flex items-center "
              onClick={onMirror}
            >
              <RefreshCcw />
            </button>
          </div>
        ) : null}
      </div>

      {isSubmitting ? (
        <div className="flex flex-col justify-center items-center">
          <Hourglass height={"32"} width={"32"} color="#1E40AF" />
          <p className="text-gray-500 mt-8">Please wait while we upload your photo...</p>
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center space-y-4">
          <button
            className="w-2/3 bg-white border border-blue-600 text-blue-600 py-3 px-4 rounded-full flex items-center justify-center space-x-2 mx-auto hover:bg-blue-600 hover:text-white transition active:scale-95 duration-150 ease-in-out"
            onClick={() => inputRef.current.click()}
          >
            <span>Retake</span>
            <Camera />
          </button>

          <button
            className="w-2/3 bg-blue-600 text-white py-3 px-4 rounded-full flex items-center justify-center space-x-2 mx-auto transition active:scale-95 duration-150 ease-in-out"
            onClick={() => onConfirm(imageBlob)}
            disabled={isSubmitting}
          >
            <span>Confirm</span>
            <CircleCheck />
          </button>
        </div>
      )}

      <input ref={inputRef} accept="image/*" type="file" capture="environment" hidden onChange={onChange} />

      <Footer /> */}
    </div>
  );
};

export default CheckQualitySelfieWithMyKad;
